import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import GoogleAddressInput from "@/components/maps/GoogleAddressInput";
import {
  quotationSigningClient,
  type PublicQuotationSigningSession,
  type QuotationSigningFormData,
} from "@/services/quotationSigningClient";
import { buildSignedQuotationFileName } from "@/utils/quotationSigningPdf";
import { normalizePhoneForStorage } from "@/utils/phone";
import { normalizePersonNameCase } from "@/utils/personName";

import "./quotationSigning.css";

type QuotationSigningTextFieldKey = Exclude<keyof Omit<QuotationSigningFormData, "checkboxes">, "paymentScheduleAcknowledged">;
type SigningPaymentStage = {
  key: string;
  stageKey: string;
  componentLabel: string;
  label: string;
  amount: number;
  reference: string;
  description?: string;
  triggerLabel?: string;
};

type SignaturePadProps = {
  disabled?: boolean;
  resetKey: number;
  onChange: (value: string | null) => void;
};

const ADDITIONAL_FEE_ITEMS: Array<{ key: keyof QuotationSigningFormData["checkboxes"]; label: string }> = [
  { key: "soilReport", label: "Soil Report" },
  { key: "legalPointOfDischarge", label: "Legal Point of Discharge" },
  { key: "wsudReport", label: "WSUD Report ($600 + GST)" },
  { key: "sewerInformation", label: "Sewer Information (SDO)" },
];

const REQUIRED_FIELD_LABELS: Array<{ key: QuotationSigningTextFieldKey; label: string }> = [
  { key: "signedByName", label: "Signed by" },
  { key: "clientName", label: "Client name" },
  { key: "clientAddress", label: "Client address" },
  { key: "clientPhone", label: "Phone" },
  { key: "clientEmail", label: "Email" },
];

function formatDateTime(value: string | null | undefined): string {
  if (!value) return "Not available";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString("en-AU", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function toTrimmedString(value: unknown): string {
  return String(value ?? "").trim();
}

function formatCurrency(value: unknown): string {
  const numeric = typeof value === "number" ? value : Number(String(value ?? "").trim());
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return "Quoted fee unavailable";
  }
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    maximumFractionDigits: 0,
  }).format(numeric);
}

function parseQuotationPaymentStages(value: unknown): SigningPaymentStage[] {
  if (!value) return [];

  let parsed: any = value;
  if (typeof value === "string") {
    try {
      parsed = JSON.parse(value);
    } catch {
      return [];
    }
  }

  const rawStages = Array.isArray(parsed)
    ? parsed
    : Array.isArray(parsed?.stages)
      ? parsed.stages
      : [];

  return rawStages
    .map((stage: any, index: number) => {
      const amount = Number(stage?.amount ?? stage?.Amount ?? 0);
      if (!Number.isFinite(amount) || amount <= 0) {
        return null;
      }

      return {
        key: toTrimmedString(stage?.key ?? stage?.Key ?? `payment-stage-${index + 1}`),
        stageKey: toTrimmedString(stage?.stageKey ?? stage?.StageKey ?? "final") || "final",
        componentLabel: toTrimmedString(stage?.componentLabel ?? stage?.ComponentLabel ?? "Payment") || "Payment",
        label: toTrimmedString(stage?.label ?? stage?.Label ?? "Payment") || "Payment",
        amount,
        reference: toTrimmedString(stage?.reference ?? stage?.Reference),
        description: toTrimmedString(stage?.description ?? stage?.Description) || undefined,
        triggerLabel: toTrimmedString(stage?.triggerLabel ?? stage?.TriggerLabel) || undefined,
      } satisfies SigningPaymentStage;
    })
    .filter((stage): stage is SigningPaymentStage => Boolean(stage));
}

function buildPaymentSchedule(session: PublicQuotationSigningSession | null): SigningPaymentStage[] {
  const quotation = session?.quotation;
  if (!quotation) return [];
  return [
    ...parseQuotationPaymentStages(quotation.StructuralPaymentScheduleJson),
    ...parseQuotationPaymentStages(quotation.CivilPaymentScheduleJson),
  ];
}

function formatPaymentStageTitle(stage: SigningPaymentStage): string {
  const component = stage.componentLabel || "Payment";
  const label = stage.label || "Stage";
  return component.toLowerCase() === label.toLowerCase() ? label : `${component} ${label}`;
}

function formatPaymentStageReference(stage: SigningPaymentStage): string {
  return stage.triggerLabel || stage.reference || "Payment milestone";
}

function buildProjectLabel(session: PublicQuotationSigningSession | null): string {
  const quotation = session?.quotation;
  if (!quotation) {
    return "Quotation";
  }

  const pieces = [
    String(quotation.ProjectDescription || "").trim(),
    String(quotation.Address || "").trim(),
    String(quotation.Suburb || "").trim(),
  ].filter(Boolean);

  return pieces.join(" | ") || `Quotation ${session?.quotationNumber || quotation.QuotationId || ""}`.trim();
}

function validateForm(form: QuotationSigningFormData) {
  const issues: Array<{ key: string; label: string; message: string }> = [];

  REQUIRED_FIELD_LABELS.forEach((field) => {
    const value = String(form[field.key] || "").trim();
    if (!value) {
      issues.push({
        key: field.key,
        label: field.label,
        message: `${field.label} is required before signing.`,
      });
    }
  });

  const normalizedEmail = String(form.clientEmail || "").trim();
  if (normalizedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
    issues.push({
      key: "clientEmail",
      label: "Email",
      message: "Enter a valid email address.",
    });
  }

  const normalizedPhone = normalizePhoneForStorage(form.clientPhone);
  if (String(form.clientPhone || "").trim() && (!normalizedPhone || normalizedPhone.replace(/\D/g, "").length < 8)) {
    issues.push({
      key: "clientPhone",
      label: "Phone",
      message: "Enter a valid phone number.",
    });
  }

  if (!form.paymentScheduleAcknowledged) {
    issues.push({
      key: "paymentScheduleAcknowledged",
      label: "Payment schedule",
      message: "Confirm you understand the payment schedule before signing.",
    });
  }

  return issues;
}

function SignaturePad({ disabled, resetKey, onChange }: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const hasInkRef = useRef(false);

  const resizeCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = Math.max(320, Math.round(rect.width * ratio));
    canvas.height = Math.round(210 * ratio);
    const context = canvas.getContext("2d");
    if (!context) return;
    context.scale(ratio, ratio);
    context.lineCap = "round";
    context.lineJoin = "round";
    context.lineWidth = 2.5;
    context.strokeStyle = "#153a60";
    context.fillStyle = "#fffdf8";
    context.fillRect(0, 0, rect.width, 210);
    hasInkRef.current = false;
    onChange(null);
  };

  useEffect(() => {
    resizeCanvas();
  }, []);

  useEffect(() => {
    resizeCanvas();
  }, [resetKey]);

  const pointForEvent = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return { x: 0, y: 0 };
    }
    const rect = canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context) return;

    const point = pointForEvent(event);
    drawingRef.current = true;
    canvas.setPointerCapture(event.pointerId);
    context.beginPath();
    context.moveTo(point.x, point.y);
    context.lineTo(point.x + 0.1, point.y + 0.1);
    context.stroke();
    hasInkRef.current = true;
    onChange(canvas.toDataURL("image/png"));
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled || !drawingRef.current) return;
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context) return;

    const point = pointForEvent(event);
    context.lineTo(point.x, point.y);
    context.stroke();
  };

  const finishStroke = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (drawingRef.current) {
      drawingRef.current = false;
      canvas.releasePointerCapture(event.pointerId);
      if (hasInkRef.current) {
        onChange(canvas.toDataURL("image/png"));
      }
    }
  };

  return (
    <div className="quotation-signing-signature">
      <canvas
        ref={canvasRef}
        className={`quotation-signing-canvas${disabled ? " quotation-signing-canvas--disabled" : ""}`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={finishStroke}
        onPointerLeave={finishStroke}
      />
      <div className="quotation-signing-signature-caption">
        Draw your signature with a finger, stylus, or mouse.
      </div>
    </div>
  );
}

export default function QuotationSigningPage() {
  const location = useLocation();
  const [session, setSession] = useState<PublicQuotationSigningSession | null>(null);
  const [form, setForm] = useState<QuotationSigningFormData>({
    signedByName: "",
    clientName: "",
    clientCompany: "",
    clientAbn: "",
    clientAddress: "",
    clientPhone: "",
    clientEmail: "",
    paymentScheduleAcknowledged: false,
    checkboxes: {
      soilReport: false,
      legalPointOfDischarge: false,
      wsudReport: false,
      sewerInformation: false,
    },
  });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitWarnings, setSubmitWarnings] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [signatureDataUrl, setSignatureDataUrl] = useState<string | null>(null);
  const [signatureResetKey, setSignatureResetKey] = useState(0);
  const [signatureNotice, setSignatureNotice] = useState<string | null>(null);

  const token = new URLSearchParams(location.search).get("token") || "";
  const validationIssues = validateForm(form);
  const missingLabels = REQUIRED_FIELD_LABELS
    .filter((field) => !String(form[field.key] || "").trim())
    .map((field) => field.label);
  const paymentScheduleIssue = validationIssues.find((item) => item.key === "paymentScheduleAcknowledged");
  const missingRequirementLabels = paymentScheduleIssue
    ? [...missingLabels, paymentScheduleIssue.label]
    : missingLabels;
  const hasBlockingIssues = validationIssues.length > 0;
  const canSubmit = session?.status === "active" && !hasBlockingIssues && Boolean(signatureDataUrl) && !isSubmitting;
  const paymentSchedule = buildPaymentSchedule(session);
  const additionalFeeItems = ADDITIONAL_FEE_ITEMS.filter((item) => form.checkboxes[item.key]);
  const reviewQuotationUrl = token ? quotationSigningClient.buildReviewQuotationUrl(token) : "";
  const reviewQuotationPdfUrl = token ? quotationSigningClient.buildReviewQuotationPdfUrl(token) : "";
  const embeddedQuotationPdfUrl = reviewQuotationPdfUrl ? `${reviewQuotationPdfUrl}#toolbar=1&navpanes=0&view=FitH` : "";
  const hasPaymentScheduleDetails = paymentSchedule.length > 0 || additionalFeeItems.length > 0;

  useEffect(() => {
    let active = true;

    const loadSession = async () => {
      if (!token) {
        setLoadError("This quotation signing link is missing its token.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setLoadError(null);
        const nextSession = await quotationSigningClient.getSession(token);
        if (!active) return;
        setSession(nextSession);
        setForm({
          ...nextSession.formDefaults,
          paymentScheduleAcknowledged: Boolean(nextSession.formDefaults.paymentScheduleAcknowledged),
        });
      } catch (error) {
        if (!active) return;
        setLoadError(error instanceof Error ? error.message : "Failed to load the quotation signing page.");
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    void loadSession();
    return () => {
      active = false;
    };
  }, [token]);

  const updateField = (field: QuotationSigningTextFieldKey, value: string) => {
    setForm((current) => {
      const nextValue = value;
      const previousValue = current[field];
      if (signatureDataUrl && previousValue !== nextValue && REQUIRED_FIELD_LABELS.some((item) => item.key === field)) {
        setSignatureDataUrl(null);
        setSignatureResetKey((currentKey) => currentKey + 1);
        setSignatureNotice("Required details changed, so the signature was cleared. Please sign again once the fields are complete.");
      }

      return {
        ...current,
        [field]: nextValue,
      };
    });
  };

  const updateCheckbox = (field: keyof QuotationSigningFormData["checkboxes"], checked: boolean) => {
    setForm((current) => ({
      ...current,
      checkboxes: {
        ...current.checkboxes,
        [field]: checked,
      },
    }));
  };

  const updatePaymentScheduleAcknowledgement = (checked: boolean) => {
    setForm((current) => ({
      ...current,
      paymentScheduleAcknowledged: checked,
    }));
  };

  const handlePhoneBlur = () => {
    setForm((current) => ({
      ...current,
      clientPhone: normalizePhoneForStorage(current.clientPhone) || current.clientPhone.trim(),
    }));
  };

  const handleClientNameBlur = () => {
    updateField("clientName", normalizePersonNameCase(form.clientName));
  };

  const handleReviewQuotation = () => {
    if (!reviewQuotationUrl) return;
    window.open(reviewQuotationUrl, "_blank", "noopener,noreferrer");
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitError(null);
    setSubmitWarnings([]);
    setSignatureNotice(null);

    if (!session || session.status !== "active") {
      setSubmitError("This quotation signing link is no longer available for signing.");
      return;
    }

    if (validationIssues.length > 0) {
      setSubmitError(validationIssues[0].message);
      return;
    }

    if (!signatureDataUrl) {
      setSubmitError("Please draw your signature before submitting.");
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await quotationSigningClient.submit({
        token,
        ...form,
        clientName: normalizePersonNameCase(form.clientName),
        clientPhone: normalizePhoneForStorage(form.clientPhone) || form.clientPhone.trim(),
        clientEmail: form.clientEmail.trim().toLowerCase(),
        signatureDataUrl,
        pdfContentType: "application/pdf",
        pdfFileName: buildSignedQuotationFileName(session.quotationNumber),
      });

      setSession(response.data);
      setForm({
        ...response.data.formDefaults,
        paymentScheduleAcknowledged: Boolean(response.data.formDefaults.paymentScheduleAcknowledged),
      });
      setSignatureDataUrl(null);
      setSignatureResetKey((currentKey) => currentKey + 1);
      setSubmitWarnings(response.warnings);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Failed to submit the signed quotation.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderReceipt = (currentSession: PublicQuotationSigningSession) => (
    <div className="quotation-signing-shell quotation-signing-shell--receipt">
      <section className="quotation-signing-card quotation-signing-card--hero">
        <div className="quotation-signing-pill quotation-signing-pill--success">Quotation accepted</div>
        <h1>Signed quotation received</h1>
        <p>
          This quotation has been signed online. The signed quotation PDF and completion certificate have been filed and emailed to the recorded recipients.
        </p>
      </section>

      <section className="quotation-signing-platform-panel">
        <div>
          <span>Online signing platform enabled by OneAdmin</span>
          <strong>Quick, secure client approvals without manual document chasing.</strong>
        </div>
        <p>
          OneAdmin helps businesses issue signing links, capture client acceptance, return signed documents, and keep an audit-ready completion record.
        </p>
      </section>

      <section className="quotation-signing-card">
        <div className="quotation-signing-summary-list">
          <div>
            <span>Quotation</span>
            <strong>{currentSession.quotationNumber || currentSession.quotationId || "Quotation"}</strong>
          </div>
          <div>
            <span>Quotation entity</span>
            <strong>{currentSession.quotation?.Entity || "Linfinity Engineering Pty Ltd"}</strong>
          </div>
          <div>
            <span>Project</span>
            <strong>{buildProjectLabel(currentSession)}</strong>
          </div>
          <div>
            <span>Signed at</span>
            <strong>{formatDateTime(currentSession.completedAt)}</strong>
          </div>
          <div>
            <span>Signed by</span>
            <strong>{currentSession.completedSummary?.signedByName || currentSession.formDefaults.signedByName || "Not recorded"}</strong>
          </div>
          <div>
            <span>Client email</span>
            <strong>{currentSession.completedSummary?.clientEmail || currentSession.formDefaults.clientEmail || "Not recorded"}</strong>
          </div>
          <div>
            <span>Email delivery</span>
            <strong>{currentSession.emailRecipients.length > 0 ? currentSession.emailRecipients.join(", ") : "Sent through PMApp mail delivery"}</strong>
          </div>
          <div>
            <span>Returned documents</span>
            <strong>Signed quotation PDF and certificate of completion</strong>
          </div>
        </div>
        {submitWarnings.length > 0 ? (
          <div className="quotation-signing-banner quotation-signing-banner--warning">
            {submitWarnings.join(" ")}
          </div>
        ) : null}
      </section>
    </div>
  );

  if (loading) {
    return (
      <div className="quotation-signing-page">
        <div className="quotation-signing-shell">
          <section className="quotation-signing-card quotation-signing-card--hero">
            <div className="quotation-signing-pill">Preparing quotation</div>
            <h1>Loading your quotation</h1>
            <p>OneAdmin is checking this signing link and preparing the quotation details.</p>
          </section>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="quotation-signing-page">
        <div className="quotation-signing-shell quotation-signing-shell--receipt">
          <section className="quotation-signing-card quotation-signing-card--hero">
            <div className="quotation-signing-pill quotation-signing-pill--danger">Link unavailable</div>
            <h1>Quotation signing cannot continue</h1>
            <p>{loadError}</p>
          </section>
        </div>
      </div>
    );
  }

  if (!session) {
    return null;
  }

  if (session.status === "completed") {
    return <div className="quotation-signing-page">{renderReceipt(session)}</div>;
  }

  if (session.status === "expired" || session.status === "revoked" || session.status === "processing") {
    return (
      <div className="quotation-signing-page">
        <div className="quotation-signing-shell quotation-signing-shell--receipt">
          <section className="quotation-signing-card quotation-signing-card--hero">
            <div className={`quotation-signing-pill quotation-signing-pill--${session.status === "processing" ? "info" : "danger"}`}>
              {session.status === "processing" ? "Processing" : "Link inactive"}
            </div>
            <h1>
              {session.status === "processing"
                ? "This quotation is being processed"
                : session.status === "expired"
                  ? "This quotation signing link has expired"
                  : "This quotation signing link is no longer active"}
            </h1>
            <p>
              {session.status === "processing"
                ? "A signing submission is already being finalized. Please refresh in a moment to check the final receipt."
                : "Please contact the PMApp team member who sent this quotation so they can issue a fresh signing link if needed."}
            </p>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="quotation-signing-page">
      <div className="quotation-signing-shell">
        <section className="quotation-signing-card quotation-signing-card--hero">
          <div className="quotation-signing-pill">Online signing enabled by OneAdmin</div>
          <h1>Review and sign your quotation</h1>
          <p>
            Please confirm the client details below and draw your signature here. Your acceptance is stamped onto the saved quotation PDF, then the signed quotation and completion certificate are emailed back to you.
          </p>
          <div className="quotation-signing-meta">
            <div>
              <span>Quotation</span>
              <strong>{session.quotationNumber || session.quotationId || "Quotation"}</strong>
            </div>
            <div>
              <span>Link expiry</span>
              <strong>{formatDateTime(session.expiresAt)}</strong>
            </div>
          </div>
        </section>

        <section className="quotation-signing-platform-panel">
          <div>
            <span>Fast, secure online approvals</span>
            <strong>Built for businesses that need signed documents returned without delay.</strong>
          </div>
          <p>
            OneAdmin keeps the signing link, client details, signature evidence, and returned PDFs together so quotation acceptance is clear and audit-ready.
          </p>
        </section>

        <section className="quotation-signing-card quotation-signing-card--pdf">
          <div className="quotation-signing-section-head">
            <div>
              <h2>Quotation PDF</h2>
              <p>Review the saved quotation while completing the signing details below.</p>
            </div>
            <button
              type="button"
              className="quotation-signing-review-button"
              onClick={handleReviewQuotation}
              disabled={!reviewQuotationUrl}
            >
              Open PDF
            </button>
          </div>
          {embeddedQuotationPdfUrl ? (
            <iframe
              className="quotation-signing-pdf-frame"
              title="Quotation PDF"
              src={embeddedQuotationPdfUrl}
            />
          ) : (
            <div className="quotation-signing-banner quotation-signing-banner--warning">
              The quotation PDF is not available for this signing link.
            </div>
          )}
        </section>

        <div className="quotation-signing-layout">
          <section className="quotation-signing-card">
            <div className="quotation-signing-section-head">
              <div>
                <h2>Quotation summary</h2>
                <p>Review the project details before signing.</p>
              </div>
              <div className="quotation-signing-summary-actions">
                <button
                  type="button"
                  className="quotation-signing-review-button"
                  onClick={handleReviewQuotation}
                  disabled={!reviewQuotationUrl}
                >
                  Review Quotation
                </button>
                <div className="quotation-signing-badge">{formatCurrency(session.quotation?.SubTotal || session.quotation?.QuotationAmount)}</div>
              </div>
            </div>
            <div className="quotation-signing-summary-list">
              <div>
                <span>Quotation entity</span>
                <strong>{session.quotation?.Entity || "Linfinity Engineering Pty Ltd"}</strong>
              </div>
              <div>
                <span>Project</span>
                <strong>{buildProjectLabel(session)}</strong>
              </div>
              <div>
                <span>Project scope</span>
                <strong>{session.quotation?.ProjectScope || "Not specified"}</strong>
              </div>
              <div>
                <span>Project type</span>
                <strong>{session.quotation?.ProjectType || "Not specified"}</strong>
              </div>
              <div>
                <span>Site</span>
                <strong>{[session.quotation?.Address, session.quotation?.Suburb, session.quotation?.PostCode].filter(Boolean).join(", ") || "Not specified"}</strong>
              </div>
              <div>
                <span>Issued date</span>
                <strong>{formatDateTime(session.issuedAt)}</strong>
              </div>
              <div>
                <span>Report add-ons</span>
                <strong>Optional before final signing</strong>
              </div>
            </div>
            {hasPaymentScheduleDetails ? (
              <div className="quotation-signing-payment-schedule">
                <div className="quotation-signing-subsection-title">Payment schedule</div>
                {paymentSchedule.length > 0 ? (
                  <div className="quotation-signing-payment-list">
                    {paymentSchedule.map((stage) => (
                      <div key={stage.key} className="quotation-signing-payment-row">
                        <div className="quotation-signing-payment-main">
                          <strong>{formatPaymentStageTitle(stage)}</strong>
                          <span>{formatPaymentStageReference(stage)}</span>
                        </div>
                        <div className="quotation-signing-payment-amount">{`${formatCurrency(stage.amount)} + GST`}</div>
                      </div>
                    ))}
                  </div>
                ) : null}
                {additionalFeeItems.length > 0 ? (
                  <div className="quotation-signing-additional-fees">
                    <div className="quotation-signing-additional-fees-title">Additional Fees as required</div>
                    <div className="quotation-signing-additional-fees-list">
                      {additionalFeeItems.map((item) => (
                        <div key={item.key} className="quotation-signing-additional-fee-row">
                          {item.label}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
          </section>

          <section className="quotation-signing-card">
            <div className="quotation-signing-section-head">
              <div>
                <h2>Client details and signature</h2>
                <p>Required fields are highlighted immediately so nothing gets missed before signing.</p>
              </div>
            </div>

            {missingRequirementLabels.length > 0 ? (
              <div className="quotation-signing-banner quotation-signing-banner--warning">
                Complete these required items before signing: {missingRequirementLabels.join(", ")}.
              </div>
            ) : (
              <div className="quotation-signing-banner quotation-signing-banner--success">
                All required details are filled. Draw the signature and submit when you are ready.
              </div>
            )}

            {submitError ? (
              <div className="quotation-signing-banner quotation-signing-banner--danger">{submitError}</div>
            ) : null}

            {signatureNotice ? (
              <div className="quotation-signing-banner quotation-signing-banner--info">{signatureNotice}</div>
            ) : null}

            <form className="quotation-signing-form" onSubmit={handleSubmit}>
              <div className="quotation-signing-grid">
                {REQUIRED_FIELD_LABELS.map((field) => {
                  const value = String(form[field.key] || "");
                  const issue = validationIssues.find((item) => item.key === field.key);
                  const inputType = field.key === "clientEmail" ? "email" : field.key === "clientPhone" ? "tel" : "text";
                  return (
                    <label key={field.key} className={`quotation-signing-field${issue ? " quotation-signing-field--error" : ""}`}>
                      <span>{field.label}</span>
                      {field.key === "clientAddress" ? (
                        <GoogleAddressInput
                          value={value}
                          onChange={(nextValue) => updateField(field.key, nextValue)}
                          placeholder={field.label}
                        />
                      ) : (
                        <input
                          type={inputType}
                          value={value}
                          onChange={(event) => updateField(field.key, event.target.value)}
                          onBlur={field.key === "clientPhone"
                            ? handlePhoneBlur
                            : field.key === "clientName"
                              ? handleClientNameBlur
                              : undefined}
                          placeholder={field.label}
                          autoComplete={field.key === "clientEmail" ? "email" : field.key === "clientPhone" ? "tel" : "on"}
                        />
                      )}
                      {issue ? <small>{issue.message}</small> : null}
                    </label>
                  );
                })}

                <label className="quotation-signing-field">
                  <span>Company</span>
                  <input
                    type="text"
                    value={form.clientCompany}
                    onChange={(event) => updateField("clientCompany", event.target.value)}
                    placeholder="Optional"
                  />
                </label>

                <label className="quotation-signing-field">
                  <span>ABN</span>
                  <input
                    type="text"
                    value={form.clientAbn}
                    onChange={(event) => updateField("clientAbn", event.target.value)}
                    placeholder="Optional"
                  />
                </label>
              </div>

              <div className="quotation-signing-addon-grid">
                <label className="quotation-signing-toggle">
                  <input
                    type="checkbox"
                    checked={form.checkboxes.soilReport}
                    onChange={(event) => updateCheckbox("soilReport", event.target.checked)}
                  />
                  <span>Include Soil Report</span>
                </label>
                <label className="quotation-signing-toggle">
                  <input
                    type="checkbox"
                    checked={form.checkboxes.sewerInformation}
                    onChange={(event) => updateCheckbox("sewerInformation", event.target.checked)}
                  />
                  <span>Include Sewer Information (SDO)</span>
                </label>
                <label className="quotation-signing-toggle">
                  <input
                    type="checkbox"
                    checked={form.checkboxes.legalPointOfDischarge}
                    onChange={(event) => updateCheckbox("legalPointOfDischarge", event.target.checked)}
                  />
                  <span>Include Legal Point of Discharge</span>
                </label>
                <label className="quotation-signing-toggle">
                  <input
                    type="checkbox"
                    checked={form.checkboxes.wsudReport}
                    onChange={(event) => updateCheckbox("wsudReport", event.target.checked)}
                  />
                  <span>Include WSUD Report ($600 + GST)</span>
                </label>
              </div>

              <label className={`quotation-signing-toggle quotation-signing-toggle--full${paymentScheduleIssue ? " quotation-signing-toggle--error" : ""}`}>
                <input
                  type="checkbox"
                  checked={form.paymentScheduleAcknowledged}
                  onChange={(event) => updatePaymentScheduleAcknowledgement(event.target.checked)}
                />
                <span>I understand and accept the payment schedule shown in this quotation.</span>
              </label>
              {paymentScheduleIssue ? (
                <small className="quotation-signing-standalone-error">{paymentScheduleIssue.message}</small>
              ) : null}

              <div className="quotation-signing-signature-wrap">
                <div className="quotation-signing-section-head quotation-signing-section-head--tight">
                  <div>
                    <h2>Signature</h2>
                    <p>The final signed PDF and completion certificate are generated from this drawing, but the raw signature image is not stored separately.</p>
                  </div>
                </div>
                <SignaturePad
                  disabled={isSubmitting}
                  resetKey={signatureResetKey}
                  onChange={(value) => setSignatureDataUrl(value)}
                />
              </div>

              <div className="quotation-signing-actions">
                <button
                  type="button"
                  className="quotation-signing-button quotation-signing-button--ghost"
                  onClick={() => {
                    setSignatureDataUrl(null);
                    setSignatureResetKey((currentKey) => currentKey + 1);
                    setSignatureNotice("Signature cleared.");
                  }}
                  disabled={isSubmitting}
                >
                  Clear signature
                </button>
                <button
                  type="submit"
                  className="quotation-signing-button"
                  disabled={!canSubmit}
                >
                  {isSubmitting ? "Submitting signed quotation..." : "Accept quotation and email signed PDF"}
                </button>
              </div>
            </form>
          </section>
        </div>
      </div>
    </div>
  );
}
